import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getWatchBrands, scanReferenceDuplicates } from '../../services/firebase';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import type { WatchBrand } from '../../types';
import type {
  DuplicateCluster,
  ScanReferenceDuplicatesResult,
} from '../../types/referenceDedup';
import { Button } from '../common/Button';
import { DuplicateClusterTable } from './DuplicateClusterTable';
import { ClusterReviewDialog } from './ClusterReviewDialog';
import { BulkMergePanel } from './BulkMergePanel';

/**
 * Reference Cleanup — Phase 1 (scan-only).
 *
 * Calls the read-only `scanReferenceDuplicates` Cloud Function and renders the
 * detected legacy duplicate clusters. No merge action — that's Phase 2 (see
 * LEGACY_REF_DEDUP_PLAN.md §6). The page is fully functional the moment the
 * backend callable is deployed
 * (`firebase deploy --only functions:scanReferenceDuplicates`).
 */
interface ReferenceCleanupPageProps {
  /** Pre-select this brand on mount (deep-link from the Enrichment Tasks panel). */
  initialBrandId?: string;
  /** Called once the initial brand has been applied, so the parent can clear it. */
  onBrandConsumed?: () => void;
  /** When true the tool is hosted inside the per-brand panel's modal: hide the
   *  page header and brand picker (brand is locked via initialBrandId). */
  embedded?: boolean;
  /**
   * Jump to a brand's edit page (where the "Dedup identity" setting lives). Used
   * by the large-cluster safety banner. When omitted (e.g. the embedded mount,
   * already inside a brand modal) the banner shows its text without a link.
   */
  onEditBrand?: (brandId: string) => void;
}

export function ReferenceCleanupPage({
  initialBrandId,
  onBrandConsumed,
  embedded = false,
  onEditBrand,
}: ReferenceCleanupPageProps = {}) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  // '' = all brands. We recommend an explicit brand for fast iteration.
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [brandSearch, setBrandSearch] = useState('');

  // Seed the brand picker once when deep-linked from the per-brand panel.
  useEffect(() => {
    if (initialBrandId) {
      setSelectedBrandId(initialBrandId);
      onBrandConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBrandId]);

  const [scanning, setScanning] = useState(false);
  // Third scan tier: also run the backend prefix pass that surfaces base/suffix
  // variants (D30 ⊂ D30.blue) the identity-key grouping misses. Off by default.
  const [includePrefixVariants, setIncludePrefixVariants] = useState(false);
  const [result, setResult] = useState<ScanReferenceDuplicatesResult | null>(null);
  // The brand id the displayed result was scanned for ('' = all). Used to label
  // the summary line correctly even after the dropdown is changed.
  const [scannedBrandId, setScannedBrandId] = useState<string>('');

  // The cluster currently open in the Phase 2 merge dialog (null = closed).
  const [reviewCluster, setReviewCluster] = useState<DuplicateCluster | null>(null);

  // Load brands once for the filter dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getWatchBrands();
        if (!cancelled) setBrands(list.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('Failed to load brands', err);
        toast.error('Failed to load brands');
      } finally {
        if (!cancelled) setLoadingBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Algolia-backed: typo-tolerant, prefix and accent-folded, and it also matches
  // alternative names, abbreviations and parent org. Falls back to the previous
  // substring filter over `brands` when Algolia is unreachable.
  const { results: filteredBrands } = useBrandSearch(brands, brandSearch);

  const handleScan = useCallback(async () => {
    setScanning(true);
    try {
      const res = await scanReferenceDuplicates({
        ...(selectedBrandId ? { brandId: selectedBrandId } : {}),
        includePrefixVariants,
      });
      setResult(res);
      setScannedBrandId(selectedBrandId);
      if (res.clusters.length === 0) {
        toast.success('Scan complete — no duplicate clusters found.');
      } else {
        toast.success(
          `Scan complete — ${res.clusters.length} cluster${
            res.clusters.length === 1 ? '' : 's'
          } found.`,
        );
      }
    } catch (err) {
      console.error('Reference duplicate scan failed', err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Scan failed: ${msg}`);
    } finally {
      setScanning(false);
    }
  }, [selectedBrandId, includePrefixVariants]);

  // After a successful merge, drop the merged cluster from the displayed table
  // (simpler than re-running the whole scan). Matched by brand + identity key.
  const handleMergeComplete = useCallback(() => {
    const merged = reviewCluster;
    setReviewCluster(null);
    if (!merged) return;
    setResult((prev) =>
      prev
        ? {
            ...prev,
            clusters: prev.clusters.filter(
              (c) =>
                !(c.brandId === merged.brandId && c.identityKey === merged.identityKey),
            ),
          }
        : prev,
    );
  }, [reviewCluster]);

  // Reviewer override: change which doc a cluster recommends as canonical,
  // without opening the merge dialog. Mutates the in-memory scan result only —
  // no backend write. The Review & Merge dialog reads `recommendedCanonicalId`
  // to seed its canonical pick, so this override flows straight through; it
  // survives until the next scan (which returns fresh server-scored results).
  const handleCanonicalOverride = useCallback(
    (cluster: DuplicateCluster, canonicalReferenceId: string) => {
      setResult((prev) =>
        prev
          ? {
              ...prev,
              clusters: prev.clusters.map((c) =>
                c.brandId === cluster.brandId && c.identityKey === cluster.identityKey
                  ? { ...c, recommendedCanonicalId: canonicalReferenceId }
                  : c,
              ),
            }
          : prev,
      );
    },
    [],
  );

  // Re-scan a specific brand after a bulk merge so the cluster table reflects
  // the post-merge state. Bulk merge may target a different brand than the one
  // currently in the scan dropdown, so this takes an explicit brandId.
  const handleBulkMergeComplete = useCallback(async (brandId: string) => {
    if (!brandId) return;
    setScanning(true);
    try {
      const res = await scanReferenceDuplicates({ brandId, includePrefixVariants });
      setResult(res);
      setScannedBrandId(brandId);
      toast.success(
        `Re-scanned — ${res.clusters.length} cluster${
          res.clusters.length === 1 ? '' : 's'
        } remaining.`,
      );
    } catch (err) {
      console.error('Post-bulk-merge re-scan failed', err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Re-scan failed: ${msg}`);
    } finally {
      setScanning(false);
    }
  }, [includePrefixVariants]);

  const scannedBrandName = useMemo(() => {
    if (!scannedBrandId) return null;
    return brands.find((b) => b.id === scannedBrandId)?.name ?? scannedBrandId;
  }, [brands, scannedBrandId]);

  const brandNameOf = useCallback(
    (brandId: string) => brands.find((b) => b.id === brandId)?.name ?? brandId,
    [brands],
  );

  // Surface which field the scan keyed each brand on ("Clustering by `<field>`"),
  // plus a warning when a model-keyed brand skipped docs with an empty `model`.
  // Derived from the backend's per-brand summaries; absent on older responses.
  const keying = useMemo(() => {
    const summaries = result?.brandSummaries ?? [];
    if (summaries.length === 0) return null;
    const modelBrands = summaries.filter((s) => s.identityField === 'model');
    const line =
      summaries.length === 1
        ? `Clustering by \`${summaries[0].identityField}\`.`
        : modelBrands.length === 0
        ? 'Clustering by `reference` (default) for all scanned brands.'
        : `Clustering by \`reference\` (default), except ${modelBrands.length} brand` +
          `${modelBrands.length === 1 ? '' : 's'} keyed by \`model\`: ` +
          `${modelBrands.map((s) => brandNameOf(s.brandId)).join(', ')}.`;
    // Empty-identity skips only matter for model-keyed brands (an empty
    // `reference` is ordinary junk that's always skipped).
    const skipWarnings = modelBrands
      .filter((s) => s.skippedNoIdentity > 0)
      .map(
        (s) =>
          `${brandNameOf(s.brandId)}: ${s.skippedNoIdentity.toLocaleString()} doc` +
          `${s.skippedNoIdentity === 1 ? '' : 's'} skipped — no \`model\` value to cluster on.`,
      );
    return { line, skipWarnings };
  }, [result, brandNameOf]);

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Reference Cleanup</h2>
          <p className="mt-1 text-sm text-gray-600">
            Scan the catalogue for <strong>legacy duplicate references</strong> — separate
            documents whose reference numbers collapse to the same identity key under{' '}
            <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">normalizeReference</code>{' '}
            (e.g. the IWC Mark XX <code className="text-xs">IW328201</code> /{' '}
            <code className="text-xs">IW3282-01</code> cluster). This is{' '}
            <strong>Phase 1: read-only detection</strong> — no merge action yet (Phase 2).
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Requires the <code className="text-xs">scanReferenceDuplicates</code> Cloud Function to
            be deployed. Scanning all brands can take up to ~9 minutes; scanning a single brand is
            much faster and recommended while iterating.
          </p>
        </div>
      )}

      <div className="max-w-md space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        {!embedded && (
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Brand</label>
            <input
              type="text"
              value={brandSearch}
              onChange={(e) => setBrandSearch(e.target.value)}
              placeholder="Search brands…"
              className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
            />
            <select
              value={selectedBrandId}
              onChange={(e) => setSelectedBrandId(e.target.value)}
              disabled={loadingBrands || scanning}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
            >
              <option value="">
                {loadingBrands ? 'Loading brands…' : 'All brands (slower)'}
              </option>
              {filteredBrands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            {!selectedBrandId && !loadingBrands && (
              <p className="mt-1 text-xs text-amber-700">
                ⚠ All-brands scan reads every reference and can be slow. Pick a single brand for
                fast iteration.
              </p>
            )}
          </div>
        )}
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includePrefixVariants}
            onChange={(e) => setIncludePrefixVariants(e.target.checked)}
            disabled={scanning}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          <span>
            Also detect <strong>prefix variants</strong>
            <span className="block text-xs text-gray-500">
              Catches base/suffix refs the identity-key scan misses — e.g.{' '}
              <code className="text-xs">D30</code> ⊂{' '}
              <code className="text-xs">D30.blue</code> /{' '}
              <code className="text-xs">D30-Red</code>. Slightly slower (extra
              per-brand pass).
            </span>
          </span>
        </label>
        <Button variant="primary" size="sm" onClick={handleScan} loading={scanning} disabled={scanning}>
          {scanning ? 'Scanning…' : 'Scan'}
        </Button>
      </div>

      {result && (
        <div className="space-y-4">
          {result.largeClusterWarning && (
            <div className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">
                ⚠️ {result.largeClusterWarning.largeClusterCount.toLocaleString()} cluster
                {result.largeClusterWarning.largeClusterCount === 1 ? ' has' : 's have'} more than{' '}
                {result.largeClusterWarning.threshold} entries (largest:{' '}
                {result.largeClusterWarning.maxClusterSize.toLocaleString()}).
              </p>
              <p className="mt-1">
                Legitimate duplicates usually pair up. Clusters of 3+ often signal that the brand's
                identity field is grouping distinct watches together. Before merging anything,
                consider switching this brand's{' '}
                <strong>Dedup identity</strong> setting from{' '}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">reference</code> to{' '}
                <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">model</code> (or vice
                versa) and re-scanning.
              </p>
              {/* Link only when a single brand was scanned and the host can navigate
                  to its editor. For an all-brands scan we can't target one brand. */}
              {scannedBrandId && onEditBrand && (
                <button
                  type="button"
                  onClick={() => onEditBrand(scannedBrandId)}
                  className="mt-2 inline-flex items-center font-medium text-amber-900 underline hover:text-amber-700"
                >
                  Open brand editor →
                </button>
              )}
            </div>
          )}
          {keying && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              <p>{keying.line}</p>
              {keying.skipWarnings.length > 0 && (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-amber-800">
                  {keying.skipWarnings.map((w, i) => (
                    <li key={i}>⚠ {w}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <p className="text-sm text-gray-700">
            Scanned <span className="font-semibold">{result.scanned.toLocaleString()}</span>{' '}
            reference{result.scanned === 1 ? '' : 's'} across{' '}
            <span className="font-semibold">{result.brandsScanned}</span> brand
            {result.brandsScanned === 1 ? '' : 's'}
            {scannedBrandName && (
              <span className="text-gray-500"> ({scannedBrandName})</span>
            )}
            . <span className="font-semibold">{result.clusters.length}</span> cluster
            {result.clusters.length === 1 ? '' : 's'} found.
          </p>

          {result.clusters.length > 0 ? (
            <DuplicateClusterTable
              clusters={result.clusters}
              onReview={setReviewCluster}
              onCanonicalChange={handleCanonicalOverride}
            />
          ) : (
            <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">
              No duplicate clusters detected for this scope.
            </div>
          )}
        </div>
      )}

      {/*
        Phase 3 bulk runner. Opt-in (collapsed by default) so it doesn't
        intimidate. INERT until James (a) deploys `bulkMergeReferenceDuplicates`
        and (b) flips RTDB `configuration/admin/bulkMergeEnabled = true` — the
        panel surfaces a banner if the flag is off. Inherits the most-recently
        scanned brand (no local selector — it can't drift from the scan filter);
        re-scans that brand after a real run.
      */}
      <BulkMergePanel
        brands={brands}
        loadingBrands={loadingBrands}
        brandId={scannedBrandId}
        scanIdentityField={
          result?.brandSummaries?.find((s) => s.brandId === scannedBrandId)
            ?.identityField
        }
        onBulkMergeComplete={handleBulkMergeComplete}
      />

      {reviewCluster && (
        <ClusterReviewDialog
          cluster={reviewCluster}
          open={reviewCluster !== null}
          onClose={() => setReviewCluster(null)}
          onMergeComplete={handleMergeComplete}
        />
      )}
    </div>
  );
}
